import {ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild} from '@angular/core';
import {ToastrService} from 'ngx-toastr';
import {DateTime} from 'luxon';
import {Subscription} from 'rxjs';
import {ActionBoardService} from './action-board.service';
import {TimestampsService} from '../timestamps.service';
import {
    ActionBoardSubjectWireDto,
    ActionBoardUserWireDto,
    ActionSeverity,
    PostActionNotice,
    ScheduledNoticeWireDto
} from '../shell-communication/action-board-operations';
import {DetectMethodChanges, isVoid} from '../utils';
import {GenericConfirmationDialogComponent} from '../generic-confirmation-dialog/generic-confirmation-dialog.component';
import {actionSubjectLabel, GENERAL_MESSAGE_LABEL} from './action-board.model';

/** Send-time display: the user's default zone, 12-hour, no seconds (owner 2026-09-22 rulings). */
const SEND_AT_FORMAT = 'ccc dd-MMM-yy hh:mm a';
const DAY_FORMAT = 'ccc dd-MMM-yy';

/**
 * CR-20260922-001 — the composer (§5.6, sketch §5A.4): audience (this subordinate / everyone I
 * manage), subject (a path of the chosen subordinate, or a General Message for either audience),
 * severity, send at + due, headline, body. Posting creates one item per recipient, as the audience
 * stands now. A posted notice can be withdrawn from its card, never edited.
 *
 * Scheduled alerts (owner screencasts + rulings 2026-09-23): an optional SEND AT time — empty = send
 * now; set = the alert waits server-side and is posted when the Shell clock reaches it, with the
 * audience resolved at send time. A time not after now is refused (here and on the server). Send at
 * and Due share one row, Send at first: the due day can never be before the send day (the due box's
 * minimum follows the send day). After a SCHEDULE the dialog stays open with a cleared form, so
 * several alerts can be batched; an immediate POST closes it as before. The dialog has two faces:
 * the form, and the list of the sender's alerts waiting to be sent (minimized cards, cancel = an X,
 * never edit), flipped by the bottom-left button, which carries the pending count. The pending face
 * keeps the form's size and scrolls inside.
 */
@Component({
    selector: 'ets-action-board-composer',
    templateUrl: './action-board-composer.component.html',
    styleUrls: ['./action-board-composer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ActionBoardComposerComponent implements OnDestroy {

    constructor(
        private readonly _service: ActionBoardService,
        private readonly _timestamps: TimestampsService,
        private readonly _toastr: ToastrService,
        private readonly _changeDetector: ChangeDetectorRef
    ) {
        // A send lands on a recipient's board as a push; the sender's pending list shrinks with it.
        this._boardChangedSubscription = this._service.boardChanged$.subscribe(() => {
            if (this.visible) {
                this.loadPending();
            }
        });
    }

    private readonly _boardChangedSubscription: Subscription;
    private _destroyed = false;
    private _opening = 0;
    private _pendingRequest = 0;
    private _subjectsRequest = 0;

    @ViewChild('formFace') formFace: ElementRef<HTMLElement>;
    @ViewChild('cancelConfirm') cancelConfirm: GenericConfirmationDialogComponent;

    @Input() subordinates: ActionBoardUserWireDto[] = [];

    @Output() posted = new EventEmitter<void>();

    visible = false;
    face: 'form' | 'pending' = 'form';
    /** The form face's rendered height, so the pending face keeps the dialog's size (owner 2026-09-23). */
    faceHeight: number | null = null;
    audience: 'user' | 'all' = 'user';
    audienceUserId: string | null = null;
    subjects: ActionBoardSubjectWireDto[] = [];
    subjectPath = 'Global';
    severity: ActionSeverity = 'yellow';
    dueDate: Date | null = null;
    /** The picker's wall clock in the user's default zone, "YYYY-MM-DDTHH:mm:ss"; null = send now. */
    sendAtWallClock: string | null = null;
    headline = '';
    body = '';
    isPosting = false;

    pending: ScheduledNoticeWireDto[] = [];
    isLoadingPending = false;
    pendingLoadError: string | null = null;
    isLoadingSubjects = false;
    cancellingId: string | null = null;

    /** Wire values stay red/orange/yellow; the user sees LEVELS (owner 2026-09-22). */
    readonly severities: { value: ActionSeverity, text: string }[] = [
        {value: 'yellow', text: 'Regular'},
        {value: 'orange', text: 'Warning'},
        {value: 'red', text: 'Critical'}
    ];

    ngOnDestroy(): void {
        this._destroyed = true;
        this.invalidateRequests();
        this._boardChangedSubscription.unsubscribe();
    }

    get title(): string {
        return this.face === 'form' ? 'New alert' : 'Alerts waiting to be sent';
    }

    get subjectChoices(): ActionBoardSubjectWireDto[] {
        const global: ActionBoardSubjectWireDto = {path: 'Global', label: GENERAL_MESSAGE_LABEL};
        if (this.audience === 'all') {
            return [global];
        }
        return [global, ...this.subjects];
    }

    get zoneLabel(): string {
        return this._timestamps.getDefaultTimezone('UTC');
    }

    /** The picked send time as a UTC ISO instant, minute precision; null = send now. */
    get sendAtUtcIso(): string | null {
        if (isVoid(this.sendAtWallClock)) {
            return null;
        }
        const dt = DateTime.fromISO(this.sendAtWallClock, {zone: this.zoneLabel});
        if (!dt.isValid) {
            return null;
        }
        return dt.set({second: 0, millisecond: 0}).toUTC().toISO();
    }

    /** The send DAY as "yyyy-MM-dd" in the user's zone; today (user's zone) when sending now. */
    get sendDayKey(): string {
        if (!isVoid(this.sendAtWallClock) && this.sendAtWallClock.length >= 10) {
            return this.sendAtWallClock.substring(0, 10);
        }
        return DateTime.local().setZone(this.zoneLabel).toFormat('yyyy-MM-dd');
    }

    /** The due box's earliest day = the send day (owner 2026-09-23: due can never be before send). */
    get dueMin(): Date {
        return dayKeyToLocalDate(this.sendDayKey);
    }

    /** Refused (owner 2026-09-23): a send time not after now, on the minute. */
    get sendTimeInPast(): boolean {
        const iso = this.sendAtUtcIso;
        if (!iso) {
            return false;
        }
        const now = DateTime.utc().set({second: 0, millisecond: 0});
        return DateTime.fromISO(iso) <= now;
    }

    get canPost(): boolean {
        if (this.isPosting || this.isLoadingSubjects) {
            return false;
        }
        if (isVoid(this.headline) || this.headline.trim().length === 0) {
            return false;
        }
        if (this.audience === 'user' && isVoid(this.audienceUserId)) {
            return false;
        }
        if (this.sendTimeInPast) {
            return false;
        }
        return true;
    }

    get postButtonText(): string {
        return this.sendAtUtcIso ? 'Schedule' : 'Post';
    }

    /** The bottom-left flip button: the pending count on the form, the way back on the list. */
    get faceButtonText(): string {
        return this.face === 'form' ? `Waiting to be sent (${this.pending.length})` : 'Back to new alert';
    }

    @DetectMethodChanges({isAsync: true})
    async show(preselectedUserId: string | null): Promise<void> {
        if (this._destroyed) { return; }
        // The board may be detached from Angular's normal checks. If the widget closed while
        // the last rendered value was true, explicitly commit false before requesting true.
        if (this.visible) {
            this.visible = false;
            this.refresh();
        }
        this.invalidateRequests();
        this.face = 'form';
        this.faceHeight = null;
        this.audience = 'user';
        this.audienceUserId = preselectedUserId && this.subordinates.some(s => s.userId === preselectedUserId)
            ? preselectedUserId
            : (this.subordinates.length > 0 ? this.subordinates[0].userId : null);
        this.subjectPath = 'Global';
        this.clearMessageFields();
        this.visible = true;
        this.refresh();
        await Promise.all([this.loadSubjects(true), this.loadPending()]);
    }

    @DetectMethodChanges()
    hide(): void {
        this.visible = false;
        this.invalidateRequests();
        this.refresh();
    }

    /** Native X/Escape closes the widget too. Render false before the next show sets it true. */
    @DetectMethodChanges()
    onHidden(event?: {component: {option(name: string): any}}): void {
        // A previous hide animation must not close a popup which has already reopened.
        if (event && event.component.option('visible')) { return; }
        this.hide();
    }

    @DetectMethodChanges({isAsync: true})
    async onAudienceChanged(): Promise<void> {
        ++this._subjectsRequest;
        this.isLoadingSubjects = false;
        if (this.audience === 'all') {
            this.subjects = [];
            this.subjectPath = 'Global';
        } else {
            await this.loadSubjects(true);
        }
        this.refresh();
    }

    @DetectMethodChanges({isAsync: true})
    async onAudienceUserChanged(): Promise<void> {
        await this.loadSubjects();
        this.refresh();
    }

    /**
     * The picker emits from its value SETTER, i.e. also during change detection when `[value]` is
     * re-bound (the Clear All lesson, 2026-09-23). Same value → nothing to do, no nested detectChanges.
     * A due day now earlier than the send day moves up to the send day.
     */
    onSendAtChanged(event: { value: string, previousValue: string }): void {
        const value = event ? (event.value || null) : null;
        if (value === this.sendAtWallClock) {
            return;
        }
        this.sendAtWallClock = value;
        if (this.dueDate && DateTime.fromJSDate(this.dueDate).toFormat('yyyy-MM-dd') < this.sendDayKey) {
            this.dueDate = this.dueMin;
        }
        this.refresh();
    }

    /**
     * The dialog is OnPush and lives in a popup: a DevExtreme value change alone does not re-evaluate
     * `canPost`, so Post stayed disabled after the headline was typed (owner smoke 2026-09-23).
     * Every field calls this.
     */
    refresh(): void {
        if (!this._destroyed) { this._changeDetector.detectChanges(); }
    }

    @DetectMethodChanges({isAsync: true})
    async post(): Promise<void> {
        if (!this.canPost) {
            return;
        }

        this.isPosting = true;
        const opening = this._opening;
        this._changeDetector.detectChanges();

        try {
            const subject = this.subjectChoices.find(s => s.path === this.subjectPath);
            const due = this.dueDate ? DateTime.fromJSDate(this.dueDate).toFormat('yyyy-MM-dd') : null;
            const sendAt = this.sendAtUtcIso;

            await this._service.postNotice(new PostActionNotice(
                this.audience,
                this.audience === 'user' ? this.audienceUserId : null,
                this.subjectPath,
                subject ? subject.label : this.subjectPath,
                this.severity,
                this.headline.trim(),
                this.body || '',
                due,
                sendAt
            ));

            if (!this._destroyed) { this.posted.emit(); }
            if (!this.isCurrentOpening(opening)) { return; }
            if (sendAt) {
                // Batching (owner 2026-09-23): the dialog stays open, the message fields clear; the
                // audience, recipient and subject stay for the next alert.
                this._toastr.success(`Alert scheduled for ${this.formatSendAt(sendAt)}`);
                this.clearMessageFields();
                await this.loadPending();
            } else {
                this._toastr.success('Alert posted');
                this.hide();
            }
        } catch (e) {
            console.error('[action-board] post failed', e);
            if (!this._destroyed) { this._toastr.error('The alert was not posted'); }
        } finally {
            this.isPosting = false;
            this.refresh();
        }
    }

    // ---------------------------------------------------------------- the pending face

    async toggleFace(): Promise<void> {
        if (this.face === 'form') {
            // Freeze the dialog at the form's size: the list scrolls inside it (owner 2026-09-23).
            const el = this.formFace ? this.formFace.nativeElement : null;
            this.faceHeight = el && el.offsetHeight > 0 ? el.offsetHeight : this.faceHeight;
            this.face = 'pending';
            this._changeDetector.detectChanges();
            await this.loadPending();
        } else {
            this.face = 'form';
            this._changeDetector.detectChanges();
        }
    }

    @DetectMethodChanges({isAsync: true})
    async loadPending(): Promise<void> {
        if (!this.visible || this._destroyed) { return; }
        const opening = this._opening;
        const request = ++this._pendingRequest;
        this.isLoadingPending = true;
        this.pendingLoadError = null;
        this.refresh();
        try {
            const pending = await this._service.loadScheduledNotices();
            if (this.isCurrentOpening(opening) && request === this._pendingRequest) {
                this.pending = pending;
            }
        } catch (e) {
            console.error('[action-board] scheduled alerts failed', e);
            if (this.isCurrentOpening(opening) && request === this._pendingRequest) {
                this.pendingLoadError = 'Could Not Refresh Scheduled Alerts. The List May Be Out of Date.';
            }
        } finally {
            if (this.isCurrentOpening(opening) && request === this._pendingRequest) {
                this.isLoadingPending = false;
                this.refresh();
            }
        }
    }

    /** Cancel one waiting alert — after a yes on the confirmation (owner 2026-09-25); a no leaves it waiting. */
    @DetectMethodChanges({isAsync: true})
    async cancelPending(notice: ScheduledNoticeWireDto, event?: Event): Promise<void> {
        if (event) {
            event.stopPropagation();
        }
        if (this.cancellingId) {
            return;
        }
        const opening = this._opening;
        // Guard before awaiting confirmation: two clicks must never open two prompts.
        this.cancellingId = notice.scheduledNoticeId;
        this.refresh();
        try {
            try {
                await this.cancelConfirm.show(['Are you sure you want to cancel this alert?']);
            } catch { return; }
            if (!this.isCurrentOpening(opening)) { return; }
            await this._service.cancelScheduledNotice(notice.scheduledNoticeId);
            if (this.isCurrentOpening(opening)) {
                this.pending = this.pending.filter(n => n.scheduledNoticeId !== notice.scheduledNoticeId);
                this._toastr.success('Alert cancelled');
            }
        } catch (e) {
            console.error('[action-board] cancel scheduled alert failed', e);
            if (!this._destroyed) { this._toastr.error('The alert was not cancelled'); }
        } finally {
            this.cancellingId = null;
            if (this.isCurrentOpening(opening)) { await this.loadPending(); }
            this.refresh();
        }
    }

    subjectText(notice: ScheduledNoticeWireDto): string {
        return actionSubjectLabel(notice);
    }

    formatSendAt(sendAtUtc: string): string {
        return this._timestamps.getDateTimeInZoneFormat(new Date(sendAtUtc), this.zoneLabel, SEND_AT_FORMAT);
    }

    /** The due day of a pending alert: its own, else the send day (the backend's default). */
    formatDue(notice: ScheduledNoticeWireDto): string {
        const key = notice.dueDayKey
            || DateTime.fromISO(notice.sendAtUtc).setZone(this.zoneLabel).toFormat('yyyy-MM-dd');
        return DateTime.fromISO(key).toFormat(DAY_FORMAT);
    }

    /** The recipient value: "Bob P." / "All users" (resolved at send time — a user gone from the tree is named so). */
    recipientText(notice: ScheduledNoticeWireDto): string {
        if (notice.audience === 'all') {
            return 'All users';
        }
        const user = this.subordinates.find(s => s.userId === notice.audienceUserId);
        return user ? user.userName : 'A user you no longer manage';
    }

    trackPending(_: number, notice: ScheduledNoticeWireDto): string {
        return notice.scheduledNoticeId;
    }

    private clearMessageFields(): void {
        this.severity = 'yellow';
        this.dueDate = null;
        this.sendAtWallClock = null;
        this.headline = '';
        this.body = '';
    }

    private async loadSubjects(selectDefault = false): Promise<void> {
        const opening = this._opening;
        const request = ++this._subjectsRequest;
        const userId = this.audienceUserId;
        if (this.audience !== 'user' || isVoid(this.audienceUserId)) {
            this.subjects = [];
            this.isLoadingSubjects = false;
            this.refresh();
            return;
        }
        this.isLoadingSubjects = true;
        this.subjects = [];
        this.refresh();
        let subjects: ActionBoardSubjectWireDto[];
        try {
            subjects = await this._service.loadSubjects(userId);
        } catch (e) {
            console.error('[action-board] subjects failed', e);
            subjects = [];
        }
        if (!this.isCurrentOpening(opening) || request !== this._subjectsRequest || this.audienceUserId !== userId) { return; }
        this.subjects = subjects;
        this.isLoadingSubjects = false;

        // CR-20260928-001: the shared '----' subject is gone. The server lists the recipient's REAL
        // portfolios (its default one included, flagged server-side); a recipient with none gets
        // Global as the only choice.
        if (this.subjectPath !== 'Global' && !this.subjects.some(s => s.path === this.subjectPath)) {
            this.subjectPath = this.subjects.length > 0 ? this.subjects[0].path : 'Global';
        }
        if (selectDefault && this.subjectPath === 'Global' && this.subjects.length > 0) {
            this.subjectPath = this.subjects[0].path;
        }
        this.refresh();
    }

    private isCurrentOpening(opening: number): boolean {
        return !this._destroyed && this.visible && opening === this._opening;
    }

    private invalidateRequests(): void {
        ++this._opening;
        ++this._pendingRequest;
        ++this._subjectsRequest;
        this.isLoadingPending = false;
        this.isLoadingSubjects = false;
    }
}

/** "yyyy-MM-dd" → a browser-local midnight Date, which is what the DevExtreme date box compares against. */
function dayKeyToLocalDate(dayKey: string): Date {
    const parts = dayKey.split('-').map(p => parseInt(p, 10));
    return new Date(parts[0], parts[1] - 1, parts[2]);
}
